// ============================================
// Lovense Widget — floating quick-control panel
//
// Bottom-right corner, only visible when paired + on game-view.
// Collapsed = small icon. Expanded = intensity + pattern + stop.
// Reads/writes the same lovenseManager methods as the Settings panel;
// both sync implicitly through localStorage + the next _refreshState.
// ============================================

import { events, Events } from '../engine/events.js';
import { lovenseManager } from '../systems/lovense.js';

const WIDGET_CSS = `
.lw{position:fixed;bottom:16px;right:16px;z-index:900;font-family:inherit;display:none}
.lw.visible{display:block}
.lw-icon{width:40px;height:40px;border-radius:50%;background:#1a1a2e;border:2px solid #333;
  cursor:pointer;display:flex;align-items:center;justify-content:center;color:#e891b9;transition:border-color .2s}
.lw-icon:hover{border-color:#e891b9}
.lw-panel{background:#1a1a2eee;border:1px solid #333;border-radius:10px;padding:10px 12px;
  width:200px;display:none;flex-direction:column;gap:8px;position:absolute;bottom:48px;right:0}
.lw-panel.open{display:flex}
.lw-hdr{display:flex;align-items:center;justify-content:space-between;font-size:.8rem;color:#e0e0e0}
.lw-stop{background:#d32f2f;color:#fff;border:none;border-radius:4px;padding:2px 8px;cursor:pointer;font-size:.75rem}
.lw-stop:hover{background:#b71c1c}
.lw-close{background:none;border:none;color:#666;cursor:pointer;font-size:1rem;padding:0;line-height:1}
.lw-close:hover{color:#e0e0e0}
.lw-row{display:flex;align-items:center;gap:6px}
.lw-slider{flex:1;height:4px;-webkit-appearance:none;background:#333;border-radius:2px;outline:none}
.lw-slider::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;background:#e891b9;border-radius:50%;cursor:pointer}
.lw-pct{font-size:.7rem;color:#888;min-width:30px;text-align:right}
.lw-pats{display:flex;gap:4px}
.lw-pat{flex:1;height:28px;background:#252540;border:1px solid #333;border-radius:6px;
  cursor:pointer;display:flex;align-items:center;justify-content:center;color:#666;transition:border-color .2s,color .2s}
.lw-pat.active{border-color:#e891b9;color:#e891b9}
.lw-pat:hover{border-color:#555;color:#aaa}
.lw-pat svg{width:20px;height:10px}
`;

const PATTERN_SVGS = {
    steady:    '<svg viewBox="0 0 20 10"><line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" stroke-width="1.5"/></svg>',
    wave:      '<svg viewBox="0 0 20 10"><polyline points="0,5 2.5,1 5,5 7.5,9 10,5 12.5,1 15,5 17.5,9 20,5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    pulse:     '<svg viewBox="0 0 20 10"><polyline points="0,8 2,8 3,2 5,2 6,8 8,8 10,2 12,2 13,8 15,8 17,2 19,2 20,8" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    heartbeat: '<svg viewBox="0 0 20 10"><polyline points="0,7 3,7 4,2 5,9 6,1 7,7 10,7 11,3 12,7 16,7 20,7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
};

const ICON_SVG = '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M8 16C4 12 4 8 8 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M16 4C20 8 20 12 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="12" cy="10" r="2.5" fill="currentColor"/></svg>';

class LovenseWidget {
    constructor() {
        this._mounted = false;
        this._root = null;
        this._styleEl = null;
        this._scope = null;
        this._onGameView = false;
    }

    init() {
        this._scope = events.createScope('lovense-widget');
        this._scope.on(Events.SCREEN_CHANGE, (screen) => {
            this._onGameView = screen === 'screen-game-view';
            this._syncVisibility();
        });
        this._scope.on(Events.LOVENSE_PAIRED, () => this._syncVisibility());
        this._scope.on(Events.LOVENSE_DISCONNECTED, () => this._syncVisibility());
        this._scope.on(Events.LOVENSE_TOY_LIST, () => this._refreshName());
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        if (this._root) {
            this._root.remove();
            this._root = null;
        }
        if (this._styleEl) {
            this._styleEl.remove();
            this._styleEl = null;
        }
        this._mounted = false;
    }

    _syncVisibility() {
        const show = this._onGameView && lovenseManager.isEnabled() && lovenseManager.isPaired();
        if (show && !this._mounted) this._mount();
        if (show && this._mounted) {
            this._root.classList.add('visible');
            this._refresh();
        }
        if (!show && this._mounted) this._root.classList.remove('visible');
    }

    _mount() {
        if (this._mounted) return;
        // Inject CSS once
        if (!this._styleEl) {
            this._styleEl = document.createElement('style');
            this._styleEl.textContent = WIDGET_CSS;
            document.head.appendChild(this._styleEl);
        }
        // Build DOM
        const el = document.createElement('div');
        el.className = 'lw';
        el.id = 'lovense-widget';

        const patBtns = Object.entries(PATTERN_SVGS).map(([name, svg]) =>
            `<button class="lw-pat" data-pattern="${name}" title="${name}">${svg}</button>`
        ).join('');

        el.innerHTML = `
            <div class="lw-icon" id="lw-toggle">${ICON_SVG}</div>
            <div class="lw-panel" id="lw-panel">
                <div class="lw-hdr">
                    <span id="lw-name">Toy</span>
                    <div style="display:flex;gap:6px;align-items:center">
                        <button class="lw-stop" id="lw-stop">Stop</button>
                        <button class="lw-close" id="lw-close">\u00d7</button>
                    </div>
                </div>
                <div class="lw-row">
                    <input type="range" class="lw-slider" id="lw-intensity" min="0" max="100" value="80">
                    <span class="lw-pct" id="lw-pct">80%</span>
                </div>
                <div class="lw-pats" id="lw-pats">${patBtns}</div>
            </div>
        `;
        document.body.appendChild(el);
        this._root = el;
        this._mounted = true;
        this._bind();
        this._refresh();
    }

    _bind() {
        const $ = (id) => this._root.querySelector('#' + id);

        $('lw-toggle').addEventListener('click', () => {
            const panel = $('lw-panel');
            panel.classList.toggle('open');
            if (panel.classList.contains('open')) this._refresh();
        });
        $('lw-close').addEventListener('click', () => $('lw-panel').classList.remove('open'));

        $('lw-stop').addEventListener('click', () => {
            lovenseManager.stop();
        });

        const slider = $('lw-intensity');
        const pct = $('lw-pct');
        slider.addEventListener('input', () => {
            const v = parseInt(slider.value, 10);
            pct.textContent = v + '%';
            lovenseManager.setIntensity(v);
        });

        $('lw-pats').addEventListener('click', (e) => {
            const btn = e.target.closest('.lw-pat');
            if (!btn) return;
            const name = btn.dataset.pattern;
            lovenseManager.setBasePattern(name);
            this._refreshPatternButtons();
        });
    }

    _refresh() {
        if (!this._mounted || !this._root) return;
        const slider = this._root.querySelector('#lw-intensity');
        const pct = this._root.querySelector('#lw-pct');
        const intensity = lovenseManager.getIntensity();
        slider.value = String(intensity);
        pct.textContent = intensity + '%';
        this._refreshName();
        this._refreshPatternButtons();
    }

    _refreshName() {
        if (!this._mounted) return;
        const nameEl = this._root.querySelector('#lw-name');
        if (!nameEl) return;
        const toys = lovenseManager.getToys();
        const names = Object.values(toys).map(t => t.name || t.id).join(', ');
        nameEl.textContent = names || 'Toy';
    }

    _refreshPatternButtons() {
        if (!this._mounted) return;
        const active = lovenseManager.getBasePattern();
        this._root.querySelectorAll('.lw-pat').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.pattern === active);
        });
    }
}

export const lovenseWidget = new LovenseWidget();

if (typeof window !== 'undefined') {
    window._lovenseWidget = lovenseWidget;
}
